/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import {
  type ReactElement,
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Provider } from 'react-aria-components';

import { useMediaQuery } from '../../hooks/use-media-query/use-media-query.hook';
import { $ } from '../../tokens.stylex';
import { TableBodyContext } from '../table-body/table-body.component';
import { TableButtonContext } from '../table-button/table-button.component';
import { View } from '../view/view.component';

interface TableOverlayContextValue<T> {
  rows: T[];
}

const TableOverlayContext = createContext<TableOverlayContextValue<unknown> | null>(null);

const ROW_HEIGHT = 36;
const HEADER_HEIGHT = 36;

export function useTableOverlayContext<T>() {
  const context = useContext(TableOverlayContext);
  if (!context) {
    throw new Error('useTableOverlayContext must be used within TableOverlay');
  }
  return context as TableOverlayContextValue<T>;
}

interface TableOverlayProps<T> {
  children: ReactNode;
  rows: T[];
  renderRowActions?: (row: T, rowIndex: number) => ReactNode;
  renderEmptyState?: () => ReactNode;
  isRowHighlighted?: (row: T) => boolean;
  /** Whether the overlay should take up the full screen. @default false */
  isFullScreen?: boolean;
  style?: stylex.StyleXStyles;
}

export function TableOverlay<T>({ children, rows, renderRowActions, renderEmptyState, isRowHighlighted, isFullScreen = false, style }: TableOverlayProps<T>): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayLayerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLElement | null>(null);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const lastMousePositionRef = useRef<{ x: number; y: number } | null>(null);

  const isDesktop = useMediaQuery('(min-width: 1024px)');

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return void 0;
    }

    const tableContainer = Array.from(container.children).find(
      (child) =>
        child.tagName !== 'STYLE' &&
        !child.hasAttribute('data-overlay-column') &&
        !child.hasAttribute('data-overlay-layer'),
    );
    if (!tableContainer) {
      return void 0;
    }

    const findScrollableElement = (element: Element): Element | null => {
      const computedStyle = window.getComputedStyle(element);

      if (computedStyle.overflow === 'auto' || computedStyle.overflowY === 'auto') {
        return element;
      }

      for (const child of element.children) {
        const scrollableElement = findScrollableElement(child);
        if (scrollableElement) {
          return scrollableElement;
        }
      }

      return null;
    };

    const scrollableContainer = findScrollableElement(tableContainer);

    if (!scrollableContainer) {
      return void 0;
    }

    const table = container.querySelector<HTMLElement>('[role="grid"]');
    if (!table) {
      return void 0;
    }
    tableRef.current = table;

    const updateRowPosition = (element: HTMLElement) => {
      const row = element.closest('[role="row"]');
      if (!row) {
        setHoveredRow(null);
        return;
      }

      const tableBody = row.closest('[role="rowgroup"]');
      if (!tableBody) {
        setHoveredRow(null);
        return;
      }

      const isVirtualized = row.hasAttribute('aria-rowindex');

      let rowIndex = row.getAttribute('aria-rowindex');
      if (rowIndex === '1' || row.closest('thead')) {
        setHoveredRow(null);
        return;
      }

      if (rowIndex === null) {
        const allRows = Array.from(tableBody.querySelectorAll('[role="row"]'));
        rowIndex = (allRows.indexOf(row) + 1).toString();
      }

      if (rowIndex) {
        setHoveredRow(isVirtualized ? Number(rowIndex) - 1 : Number(rowIndex));
      }
    };

    const handleMouseMove = (event: MouseEvent) => {
      lastMousePositionRef.current = { x: event.clientX, y: event.clientY };
      updateRowPosition(event.target as HTMLElement);
    };

    const handleOverlayMouseMove = (event: MouseEvent) => {
      lastMousePositionRef.current = { x: event.clientX, y: event.clientY };
      // Buttons in the overlay intercept pointer events from the table, so mousemove on the table
      // stops firing when the cursor is over an action button. Pierce through the overlay stack to
      // find the underlying table row and keep hoveredRow in sync without any lag.
      const elements = document.elementsFromPoint(event.clientX, event.clientY);
      const row = elements.find(
        (el) => el.getAttribute('role') === 'row' && !el.closest('thead'),
      );
      if (row) {
        updateRowPosition(row as HTMLElement);
      }
    };

    const handleMouseLeave = (event: MouseEvent) => {
      const relatedTarget = event.relatedTarget as HTMLElement;

      if (relatedTarget !== scrollableContainer && !overlayLayerRef.current?.contains(relatedTarget)) {
        setHoveredRow(null);
        lastMousePositionRef.current = null;
      }
    };

    const handleScroll = () => {
      setScrollTop(scrollableContainer.scrollTop);
      setHoveredRow(null); // Clear hover state while scrolling
    };

    const handleScrollEnd = () => {
      // Re-detect hover after scroll ends using last known mouse position
      if (lastMousePositionRef.current) {
        const elementAtPoint = document.elementFromPoint(
          lastMousePositionRef.current.x,
          lastMousePositionRef.current.y,
        );
        if (elementAtPoint) {
          updateRowPosition(elementAtPoint as HTMLElement);
        }
      }
    };

    const overlayLayer = overlayLayerRef.current;

    table.addEventListener('mousemove', handleMouseMove);
    table.addEventListener('mouseleave', handleMouseLeave);
    overlayLayer?.addEventListener('mousemove', handleOverlayMouseMove);
    scrollableContainer.addEventListener('scroll', handleScroll, { passive: true });
    scrollableContainer.addEventListener('scrollend', handleScrollEnd);

    return () => {
      table.removeEventListener('mousemove', handleMouseMove);
      table.removeEventListener('mouseleave', handleMouseLeave);
      overlayLayer?.removeEventListener('mousemove', handleOverlayMouseMove);
      scrollableContainer.removeEventListener('scroll', handleScroll);
      scrollableContainer.removeEventListener('scrollend', handleScrollEnd);
    };
  }, [children]);

  useEffect(() => {
    const table = tableRef.current;
    if (!table) {
      return;
    }

    const previousHovered = table.querySelector('[data-row-hovered="true"]');
    if (previousHovered) {
      previousHovered.removeAttribute('data-row-hovered');
    }

    if (hoveredRow === null || hoveredRow <= 0) {
      return;
    }

    const row = table.querySelector(`[role="row"][aria-rowindex="${hoveredRow + 1}"]`);
    if (row) {
      row.setAttribute('data-row-hovered', 'true');
    }
  }, [hoveredRow]);

  const showEmptyState = renderEmptyState && rows.length === 0;
  const rowHeight = ROW_HEIGHT;
  const headerHeight = HEADER_HEIGHT;

  // Calculate visible row range (for performance with large datasets)
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
  const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - 1);
  const endIndex = Math.min(rows.length, Math.ceil((scrollTop + viewportHeight) / rowHeight) + 1);

  // Find highlighted rows in visible range
  const highlightedRows = isRowHighlighted
    ? rows
      .map((row, index) => ({ row, index, isHighlighted: isRowHighlighted(row) }))
      .filter((item) => item.isHighlighted && item.index >= startIndex && item.index < endIndex)
    : [];

  return (
    <TableOverlayContext.Provider value={{ rows }}>
      <Provider values={[[TableBodyContext, { items: rows as Iterable<object> }]]}>
        <View ref={containerRef} style={[tableOverlayContainerStyles.container, !isFullScreen && tableOverlayContainerStyles.embedded, style]}>
          <View style={tableWrapperStyles.wrapper}>
            {children}

            {/* Render overlay layer above table body, below header */}
            <View data-overlay-layer ref={overlayLayerRef} style={overlayLayerStyles.layer}>
              {/* Render highlight overlays for highlighted rows */}
              {highlightedRows.map(({ index }) => {
                const top = index * rowHeight - scrollTop + headerHeight;
                return (
                  <View
                    key={`highlight-${index}`}
                    style={[
                      overlayRowStyles.base(top),
                      highlightedRowStyles.highlight,
                    ]}
                  />
                );
              })}

              {/* Render action toolbar for all visible rows; hide non-hovered rows on desktop via styles */}
              {rows.slice(startIndex, endIndex).map((row, relativeIndex) => {
                const index = startIndex + relativeIndex;
                const top = index * rowHeight - scrollTop + headerHeight;
                const isActiveRow = hoveredRow !== null && hoveredRow - 1 === index;
                const isVisible = !isDesktop || isActiveRow;

                return (
                  <View
                    key={`actions-${index}`}
                    style={overlayRowStyles.base(top)}>
                    <View
                      role="toolbar"
                      style={[
                        overlayActionsStyles.actions,
                        !isVisible && overlayActionsStyles.hidden,
                      ]}>
                      <Provider values={[
                        [
                          TableButtonContext, {
                            excludeFromTabOrder: isDesktop ? !isActiveRow : false,
                            style: [tableButtonStyle.visible],
                          },
                        ],
                      ]}>
                        {renderRowActions?.(row, index)}
                      </Provider>
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
          {showEmptyState && (
            <View style={emptyStateStyles.base}>
              {renderEmptyState()}
            </View>
          )}
        </View>
      </Provider>
    </TableOverlayContext.Provider>
  );
}

const tableOverlayContainerStyles = stylex.create({
  container: {
    position: 'relative',
    height: '100%',
    width: '100%',
  },
  embedded: {
    height: 'fit-content',
  },
});

const overlayLayerStyles = stylex.create({
  layer: {
    boxSizing: 'border-box',
    clipPath: `inset(${HEADER_HEIGHT}px 0 0 0)`,
    pointerEvents: 'none',
    position: 'absolute',
    zIndex: 2,
    height: '100%',
    left: 0,
    top: 0,
    width: '100%',
  },
});

const tableWrapperStyles = stylex.create({
  wrapper: {
    position: 'relative',
    zIndex: 0,
    height: '100%',
    width: '100%',
  },
});

const overlayRowStyles = stylex.create({
  base: (topPosition: number) => ({
    pointerEvents: 'none',
    position: 'absolute',
    zIndex: 1,
    height: ROW_HEIGHT,
    left: 0,
    right: 0,
    top: topPosition,
    width: '100%',
  }),
});

const highlightedRowStyles = stylex.create({
  highlight: {
    borderColor: $.accentChilli60,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
    boxShadow: `0 0 8px 4px ${$.colorChilli10}`,
    zIndex: 3,
  },
});

const overlayActionsStyles = stylex.create({
  actions: {
    gap: 6,
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'flex-end',
    pointerEvents: 'none',
    height: '100%',
    width: '100%',
    paddingRight: 8,
  },
  hidden: {
    opacity: 0,
    pointerEvents: 'none',
  },
});

const emptyStateStyles = stylex.create({
  base: {
    paddingBlock: ROW_HEIGHT,
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    pointerEvents: 'none',
    position: 'absolute',
    height: 136,
    left: 0,
    top: HEADER_HEIGHT,
    width: '100%',
  },
});

const tableButtonStyle = stylex.create({
  visible: {
    opacity: 1,
    pointerEvents: 'auto',
  },
});
